"use client";
import { BreadcrumbItem, BreadcrumbRoot, BreadcrumbSeparator } from "@/components/ui/Breadcrumb";
import { useParams } from "next/navigation";
import React, { Fragment, type ComponentProps, useEffect, useState } from "react";

import { readBreadcrumbSourceSegments } from "@/features/products/breadcrumbSource";
import { useLang, useTranslations } from "@/i18n/client";
import { routing } from "@/i18n/routing";
import { resolveBreadcrumbName } from "@/lib/utils";
import { addLeadingSlash } from "@frend-digital/utils";

export const PathBreadcrumbs = ({
	title,
	segments: segmentsProp,
	nonLinkableSegments,
	productId,
	...props
}: {
	title?: string;
	segments?: string[];
	nonLinkableSegments?: string[];
	productId?: string;
} & ComponentProps<"ol">) => {
	const { slug } = useParams<{ slug: string[] }>();

	const t = useTranslations();
	const lang = useLang();

	const baseSegments = segmentsProp ?? slug.filter((s) => !routing.groups.includes(s));

	// Client-only: if the customer arrived from a collection (recorded on click), swap the
	// visible trail to that collection. SSR/first paint renders baseSegments (canonical),
	// so there is no hydration mismatch and crawlers see the canonical trail. Re-reads on
	// `lang` change too: the stored path's slugs belong to whatever language it was recorded
	// in, so a language switch that doesn't remount this component (a soft `[geo]` transition)
	// must drop or re-validate the source trail rather than keep showing the old language.
	const [sourceSegments, setSourceSegments] = useState<string[] | null>(null);
	useEffect(() => {
		setSourceSegments(readBreadcrumbSourceSegments(productId, lang));
	}, [productId, lang]);

	// When a source trail is active, the leaf keeps the last canonical segment purely as a
	// placeholder: it renders isLast, so resolveBreadcrumbName shows the product `title` and
	// its href is never used. Do not wire this segment into a link in a future refactor.
	const leaf = baseSegments[baseSegments.length - 1] ?? "";
	const segments = sourceSegments ? [...sourceSegments, leaf] : baseSegments;

	return (
		<BreadcrumbRoot {...props}>
			{segments.map((slug, index, arr) => {
				// The href is the combination of all parent slugs
				const href = addLeadingSlash(arr.slice(0, index + 1).join("/"));
				const isLast = index === arr.length - 1;
				const isLinkable = !isLast && !nonLinkableSegments?.includes(slug);
				const name = resolveBreadcrumbName({ segment: slug, isLast, title, t });

				return (
					<Fragment key={href}>
						{isLinkable ? (
							<BreadcrumbItem key={href} href={href}>
								{name}
							</BreadcrumbItem>
						) : (
							<BreadcrumbItem key={href}>
								<span style={{ textDecoration: "underline", textUnderlineOffset: "2px" }}>
									{name}
								</span>
							</BreadcrumbItem>
						)}
						<BreadcrumbSeparator />
					</Fragment>
				);
			})}
		</BreadcrumbRoot>
	);
};
